/**
 * Similar to Mantine's useLocalStorage/useSessionStorage hooks but without triggering re-renders.
 * Use these when you need to persist data that doesn't need UI updates (e.g., IDs, preferences).
 * Use Mantine's hooks when the stored value needs to be displayed in the UI or needs to trigger re-renders.
 */

interface StatelessStorageProps<T> {
  key: string;
  defaultValue?: T;
}

export function useStatelessSessionStorage<T>({ key, defaultValue }: StatelessStorageProps<T>) {
  const getItem = () => {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  };

  const setItem = (value: T) => {
    sessionStorage.setItem(key, JSON.stringify(value));
  };

  const clearItem = () => {
    sessionStorage.removeItem(key);
  };

  return { getItem, setItem, clearItem };
}

export function useStatelessLocalStorage<T>({ key, defaultValue }: StatelessStorageProps<T>) {
  const getItem = () => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  };

  const setItem = (value: T) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  const clearItem = () => {
    localStorage.removeItem(key);
  };

  return { getItem, setItem, clearItem };
}
